import { CELL_SIZE } from "./config.ts";

/** True if grid cell (gridX, gridY) blocks line of sight (firewall/obstacle/blocker). */
export type CellBlockedPredicate = (gridX: number, gridY: number) => boolean;

/**
 * Raster-walk the segment A→B in pixel space, sampling every grid cell it crosses.
 * Returns false if any intermediate cell is blocked. Start/end cells are excluded.
 */
export function hasLineOfSight(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  isBlocked: CellBlockedPredicate,
  cellSize: number = CELL_SIZE,
): boolean {
  const startX = Math.floor(ax / cellSize);
  const startY = Math.floor(ay / cellSize);
  const endX = Math.floor(bx / cellSize);
  const endY = Math.floor(by / cellSize);

  if (startX === endX && startY === endY) return true;

  const dx = bx - ax;
  const dy = by - ay;

  let stepX = 0;
  let stepY = 0;
  let tDeltaX = Infinity;
  let tDeltaY = Infinity;
  let tMaxX = Infinity;
  let tMaxY = Infinity;

  if (dx > 0) {
    stepX = 1;
    tDeltaX = cellSize / dx;
    tMaxX = ((startX + 1) * cellSize - ax) / dx;
  } else if (dx < 0) {
    stepX = -1;
    tDeltaX = cellSize / -dx;
    tMaxX = (ax - startX * cellSize) / -dx;
  }

  if (dy > 0) {
    stepY = 1;
    tDeltaY = cellSize / dy;
    tMaxY = ((startY + 1) * cellSize - ay) / dy;
  } else if (dy < 0) {
    stepY = -1;
    tDeltaY = cellSize / -dy;
    tMaxY = (ay - startY * cellSize) / -dy;
  }

  let x = startX;
  let y = startY;

  while (x !== endX || y !== endY) {
    if (tMaxX < tMaxY) {
      x += stepX;
      tMaxX += tDeltaX;
    } else if (tMaxY < tMaxX) {
      y += stepY;
      tMaxY += tDeltaY;
    } else {
      x += stepX;
      y += stepY;
      tMaxX += tDeltaX;
      tMaxY += tDeltaY;
    }

    const isEndpoint = x === endX && y === endY;
    if (!isEndpoint && isBlocked(x, y)) return false;
  }

  return true;
}
